import { getSubscriptionStatus } from '../../../services/stripeService';

/** Paid-through statuses — member must not be sent through checkout again. */
const PAID_PRIMARY_STATUSES = new Set(['active', 'trialing']);

/**
 * Statuses that mean "do not charge again" after a create conflict, including
 * past_due while a concurrent retry may still be finishing.
 */
const CONFLICT_PRIMARY_STATUSES = new Set(['active', 'trialing', 'past_due']);

/**
 * Every way the API can tell us "do not create another subscription for this
 * member". Each of these means a charge either already succeeded or is still
 * running, so the member must be recovered into success rather than shown an
 * error that invites them to pay again.
 */
const CONFLICT_MESSAGE_PATTERNS = [
    /already exists/i,
    /resource already exists/i,
    /already have an active membership/i,
    /checkout is already in progress/i,
    /payment already went through/i,
];

export function isSubscriptionConflictError(message: string | undefined): boolean {
    if (!message) return false;
    return CONFLICT_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
}

const RECOVERY_ATTEMPTS = 3;
const RECOVERY_DELAY_MS = 1500;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function readRecoveredSubscriptionId(statusData: {
    primarySubscription?: {
        stripeSubscriptionId?: string;
        _id?: string;
        status?: string;
    } | null;
    hasPrimarySubscription?: boolean;
}): string {
    return (
        statusData.primarySubscription?.stripeSubscriptionId ||
        statusData.primarySubscription?._id ||
        'recovered'
    );
}

function hasPrimaryInStatuses(
    statusData: {
        primarySubscription?: { status?: string } | null;
        hasPrimarySubscription?: boolean;
    },
    allowed: Set<string>,
): boolean {
    const primaryStatus = statusData.primarySubscription?.status;
    return Boolean(
        statusData.hasPrimarySubscription ||
            (primaryStatus != null && allowed.has(primaryStatus)),
    );
}

/**
 * After a conflict on subscription create, Stripe may have charged while Mongo
 * failed, or a concurrent create may still be finishing. Poll backend status and
 * treat an active primary sub as success.
 */
export async function recoverSubscriptionAfterConflict(): Promise<{ subscriptionId: string } | null> {
    for (let attempt = 0; attempt < RECOVERY_ATTEMPTS; attempt++) {
        // A conflict raised by a still-running create resolves a moment later,
        // so give it a chance to land before giving up.
        if (attempt > 0) {
            await wait(RECOVERY_DELAY_MS);
        }

        try {
            const status = await getSubscriptionStatus();
            if (!status.success || !status.data) continue;
            if (!hasPrimaryInStatuses(status.data, CONFLICT_PRIMARY_STATUSES)) continue;
            return { subscriptionId: readRecoveredSubscriptionId(status.data) };
        } catch {
            // Fall through to the next attempt.
        }
    }

    return null;
}

/**
 * When a member returns to the payment step after a successful charge (browser
 * crash, stuck account-creation UX, refreshed tab), skip checkout and continue
 * the funnel. Only active/trialing — past_due still needs a real payment retry.
 */
export async function recoverAlreadyPaidPrimaryOnReturn(): Promise<{ subscriptionId: string } | null> {
    try {
        const status = await getSubscriptionStatus();
        if (!status.success || !status.data) return null;

        const primaryStatus = status.data.primarySubscription?.status;
        const isPaidThrough =
            primaryStatus != null
                ? PAID_PRIMARY_STATUSES.has(primaryStatus)
                : Boolean(status.data.hasPrimarySubscription);

        if (!isPaidThrough) return null;
        return { subscriptionId: readRecoveredSubscriptionId(status.data) };
    } catch {
        return null;
    }
}
